import React, { useEffect, useLayoutEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import PortfolioPage from "@/components/portfolio/PortfolioPage";
import AdminPanel from "@/components/admin/AdminPanel";
import SeismicCaseStudyPage from "@/components/casestudy/SeismicCaseStudyPage";
import MyOperatorCaseStudyPage from "@/components/casestudy/MyOperatorCaseStudyPage";
import WorkhoursCaseStudyPage from "@/components/casestudy/WorkhoursCaseStudyPage";
import BlogHomePage from "@/components/blog/BlogHomePage";
import AiSearchCrawlingPostPage from "@/components/blog/AiSearchCrawlingPostPage";
import WhyRankOnGoogleNotChatGPTPostPage from "@/components/blog/WhyRankOnGoogleNotChatGPTPostPage";
import MeasureAiVisibilityPostPage from "@/components/blog/MeasureAiVisibilityPostPage";
import AiVisibilityAuditPostPage from "@/components/blog/AiVisibilityAuditPostPage";
import KeywordResearchAiSearchPostPage from "@/components/blog/KeywordResearchAiSearchPostPage";
import ContentWritingAiVisibilityPostPage from "@/components/blog/ContentWritingAiVisibilityPostPage";
import AiVisibilityAuditPage from "@/components/audit/AiVisibilityAuditPage";
import AiVisibilityAuditDemoPage from "@/components/audit/AiVisibilityAuditDemoPage";
import { TopRouteLoader } from "@/components/common/StylishLoader";

// Prevent browser from restoring scroll position automatically across routes
if (typeof window !== "undefined" && "scrollRestoration" in window.history) {
  window.history.scrollRestoration = "manual";
}

function ScrollToTop() {
  const { pathname, hash } = useLocation();

  // Instantly place scroll position at 0 BEFORE browser paint to eliminate upward sliding
  useLayoutEffect(() => {
    if (!hash) {
      const html = document.documentElement;
      const prevBehavior = html.style.scrollBehavior;
      html.style.scrollBehavior = "auto";
      window.scrollTo(0, 0);
      document.body.scrollTop = 0;
      html.scrollTop = 0;
      html.style.scrollBehavior = prevBehavior;
    }
  }, [pathname, hash]);

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      } else {
        const timer = setTimeout(() => {
          const el = document.getElementById(id);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 150);
        return () => clearTimeout(timer);
      }
    }
  }, [pathname, hash]);

  return null;
}

function TrailingSlashEnforcer() {
  const { pathname, search, hash } = useLocation();

  useEffect(() => {
    // If route is not root and does not end with trailing slash, normalize browser URL to ending slash
    if (pathname && pathname !== "/" && !pathname.endsWith("/")) {
      window.history.replaceState(null, "", `${pathname}/${search}${hash}`);
    }
  }, [pathname, search, hash]);

  return null;
}

function AnimatedRoutes() {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  const prevPathRef = useRef(location.pathname);

  // Trigger top laser progress bar on route transitions
  useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      setIsNavigating(true);
      const timer = setTimeout(() => setIsNavigating(false), 550);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  return (
    <>
      <TopRouteLoader isNavigating={isNavigating} />
      <div key={location.pathname} className="page-route-transition">
        <Routes location={location}>
          <Route path="/" element={<PortfolioPage />} />
          <Route path="/portfolio" element={<Navigate to="/" replace />} />
          <Route path="/portfolio/" element={<Navigate to="/" replace />} />
          <Route path="/casestudy/seismic" element={<SeismicCaseStudyPage />} />
          <Route path="/casestudy/seismic/" element={<SeismicCaseStudyPage />} />
          <Route path="/case-study/seismic" element={<Navigate to="/casestudy/seismic/" replace />} />
          <Route path="/case-study/seismic/" element={<Navigate to="/casestudy/seismic/" replace />} />
          <Route path="/casestudy/myoperator" element={<MyOperatorCaseStudyPage />} />
          <Route path="/casestudy/myoperator/" element={<MyOperatorCaseStudyPage />} />
          <Route path="/case-study/myoperator" element={<Navigate to="/casestudy/myoperator/" replace />} />
          <Route path="/case-study/myoperator/" element={<Navigate to="/casestudy/myoperator/" replace />} />
          <Route path="/casestudy/workhours" element={<WorkhoursCaseStudyPage />} />
          <Route path="/casestudy/workhours/" element={<WorkhoursCaseStudyPage />} />
          <Route path="/case-study/workhours" element={<Navigate to="/casestudy/workhours/" replace />} />
          <Route path="/case-study/workhours/" element={<Navigate to="/casestudy/workhours/" replace />} />
          <Route path="/casestudy/trydoschool" element={<Navigate to="/casestudy/workhours/" replace />} />
          <Route path="/casestudy/trydoschool/" element={<Navigate to="/casestudy/workhours/" replace />} />
          <Route path="/casestudy/trydo-school" element={<Navigate to="/casestudy/workhours/" replace />} />
          <Route path="/casestudy/trydo-school/" element={<Navigate to="/casestudy/workhours/" replace />} />
          <Route path="/admin" element={<AdminPanel />} />
          <Route path="/admin/" element={<AdminPanel />} />
          <Route path="/blog" element={<BlogHomePage />} />
          <Route path="/blog/" element={<BlogHomePage />} />
          <Route
            path="/blog/can-ai-search-engines-crawl-understand-cite-website"
            element={<AiSearchCrawlingPostPage />}
          />
          <Route
            path="/blog/can-ai-search-engines-crawl-understand-cite-website/"
            element={<AiSearchCrawlingPostPage />}
          />
          <Route
            path="/blog/ai-search-crawling-understanding-citations"
            element={<Navigate to="/blog/can-ai-search-engines-crawl-understand-cite-website/" replace />}
          />
          <Route
            path="/blog/ai-search-crawling-understanding-citations/"
            element={<Navigate to="/blog/can-ai-search-engines-crawl-understand-cite-website/" replace />}
          />
          <Route
            path="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt"
            element={<WhyRankOnGoogleNotChatGPTPostPage />}
          />
          <Route
            path="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/"
            element={<WhyRankOnGoogleNotChatGPTPostPage />}
          />
          <Route
            path="/blog/why-rank-on-google-not-chatgpt"
            element={<Navigate to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/" replace />}
          />
          <Route
            path="/blog/why-rank-on-google-not-chatgpt/"
            element={<Navigate to="/blog/why-do-i-rank-on-google-but-dont-appear-in-chatgpt/" replace />}
          />
          <Route
            path="/blog/how-to-measure-ai-search-visibility-for-free"
            element={<MeasureAiVisibilityPostPage />}
          />
          <Route
            path="/blog/how-to-measure-ai-search-visibility-for-free/"
            element={<MeasureAiVisibilityPostPage />}
          />
          <Route
            path="/blog/measure-ai-search-visibility-free"
            element={<Navigate to="/blog/how-to-measure-ai-search-visibility-for-free/" replace />}
          />
          <Route
            path="/blog/measure-ai-search-visibility-free/"
            element={<Navigate to="/blog/how-to-measure-ai-search-visibility-for-free/" replace />}
          />
          <Route
            path="/how-to-audit-a-website-for-ai-visibility"
            element={<AiVisibilityAuditPostPage />}
          />
          <Route
            path="/how-to-audit-a-website-for-ai-visibility/"
            element={<AiVisibilityAuditPostPage />}
          />
          <Route
            path="/blog/how-to-audit-a-website-for-ai-visibility"
            element={<AiVisibilityAuditPostPage />}
          />
          <Route
            path="/blog/how-to-audit-a-website-for-ai-visibility/"
            element={<AiVisibilityAuditPostPage />}
          />
          <Route
            path="/blog/how-to-audit-website-for-ai-visibility-chatgpt-google-claude"
            element={<Navigate to="/how-to-audit-a-website-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-to-audit-website-for-ai-visibility-chatgpt-google-claude/"
            element={<Navigate to="/how-to-audit-a-website-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/audit-website-for-ai-visibility"
            element={<Navigate to="/how-to-audit-a-website-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/audit-website-for-ai-visibility/"
            element={<Navigate to="/how-to-audit-a-website-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-do-i-audit-my-website-for-better-ai-visibility"
            element={<Navigate to="/how-to-audit-a-website-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-do-i-audit-my-website-for-better-ai-visibility/"
            element={<Navigate to="/how-to-audit-a-website-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-to-do-keyword-research-for-ai-visibility"
            element={<KeywordResearchAiSearchPostPage />}
          />
          <Route
            path="/blog/how-to-do-keyword-research-for-ai-visibility/"
            element={<KeywordResearchAiSearchPostPage />}
          />
          <Route
            path="/blog/how-to-do-keyword-research-for-ai-search"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-to-do-keyword-research-for-ai-search/"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-for-ai-visibility"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-for-ai-visibility/"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-for-ai-search"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-for-ai-search/"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-ai-visibility"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-ai-visibility/"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-ai-search"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/keyword-research-ai-search/"
            element={<Navigate to="/blog/how-to-do-keyword-research-for-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility"
            element={<ContentWritingAiVisibilityPostPage />}
          />
          <Route
            path="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/"
            element={<ContentWritingAiVisibilityPostPage />}
          />
          <Route
            path="/blog/how-to-write-content-for-seo-and-ai-visibility"
            element={<Navigate to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/" replace />}
          />
          <Route
            path="/blog/how-to-write-content-for-seo-and-ai-visibility/"
            element={<Navigate to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/" replace />}
          />
          <Route
            path="/blog/write-content-that-ranks-on-google-and-improves-ai-visibility"
            element={<Navigate to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/" replace />}
          />
          <Route
            path="/blog/write-content-that-ranks-on-google-and-improves-ai-visibility/"
            element={<Navigate to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/" replace />}
          />
          <Route
            path="/blog/ai-visibility-content-writing"
            element={<Navigate to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/" replace />}
          />
          <Route
            path="/blog/ai-visibility-content-writing/"
            element={<Navigate to="/blog/how-to-write-content-that-ranks-on-google-and-improves-ai-visibility/" replace />}
          />
          <Route path="/ai-visibility-audit" element={<AiVisibilityAuditPage />} />
          <Route path="/ai-visibility-audit/" element={<AiVisibilityAuditPage />} />
          <Route path="/audit" element={<Navigate to="/ai-visibility-audit/" replace />} />
          <Route path="/audit/" element={<Navigate to="/ai-visibility-audit/" replace />} />
          <Route path="/ai-audit" element={<Navigate to="/ai-visibility-audit/" replace />} />
          <Route path="/ai-audit/" element={<Navigate to="/ai-visibility-audit/" replace />} />
          <Route path="/ai-visibility-audit-demo" element={<AiVisibilityAuditDemoPage />} />
          <Route path="/ai-visibility-audit-demo/" element={<AiVisibilityAuditDemoPage />} />
          <Route path="/demo/ai-visibility-audit" element={<Navigate to="/ai-visibility-audit-demo/" replace />} />
          <Route path="/demo/ai-visibility-audit/" element={<Navigate to="/ai-visibility-audit-demo/" replace />} />
          {/* All other routes fallback to the main portfolio page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  useEffect(() => {
    if (typeof window !== "undefined" && window.__reactReady) {
      window.__reactReady();
    }
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <TrailingSlashEnforcer />
      <AnimatedRoutes />
    </BrowserRouter>
  );
}

export default App;
